// Route chunk prefetching.
//
// Every top-level page in `App.tsx` is `React.lazy`-loaded, so the first
// navigation to a module pays the cost of downloading its JS chunk — the
// perceived blank/spinner gap. Vite dedupes dynamic `import()` by module
// specifier, so warming the *same* specifier here resolves the lazy boundary
// in `App.tsx` instantly once the click happens.
//
// The importers below intentionally mirror the specifiers used by the
// `lazyRetry(() => import("@/pages/..."))` declarations in `App.tsx`. Keep the
// two in sync: a drifted specifier simply means that route is not warmed (a
// soft miss), never a duplicate chunk.

type Importer = () => Promise<unknown>;

// Keyed by the base path of each navigable module (query string stripped).
const ROUTE_IMPORTERS: Record<string, Importer> = {
  "/": () => import("@/pages/dashboard"),
  "/ar": () => import("@/pages/ar/index"),
  "/ap": () => import("@/pages/ap/index"),
  "/inbox": () => import("@/pages/inbox/index"),
  "/automations": () => import("@/pages/automations/index"),
  "/reports": () => import("@/pages/reports/index"),
  "/chat": () => import("@/pages/chat/index"),
  "/tasks": () => import("@/pages/tasks/index"),
  "/communications": () => import("@/pages/communications/index"),
  "/settings": () => import("@/pages/settings/layout"),
  "/financing": () => import("@/pages/financing/index"),
  "/operations": () => import("@/pages/operations/index"),
  "/system-health": () => import("@/pages/system-health"),
};

const prefetched = new Set<string>();
const inFlight = new Set<string>();

function basePath(url: string): string {
  const q = url.indexOf("?");
  const path = q === -1 ? url : url.slice(0, q);
  return path || "/";
}

/**
 * Warm the JS chunk for a navigable route. Safe to call repeatedly (hover,
 * focus, pointerdown) — each module is fetched at most once. A failed prefetch
 * is forgotten so a later attempt (or the real navigation) can retry.
 */
export function prefetchRoute(url: string): void {
  const path = basePath(url);
  const importer = ROUTE_IMPORTERS[path];
  if (!importer || prefetched.has(path) || inFlight.has(path)) return;
  inFlight.add(path);
  void importer()
    .then(() => {
      prefetched.add(path);
    })
    .catch(() => {
      /* leave unmarked so a future hover/navigation can retry */
    })
    .finally(() => {
      inFlight.delete(path);
    });
}

type IdleScheduler = (cb: () => void) => void;

const scheduleIdle: IdleScheduler = (cb) => {
  if (typeof window === "undefined") return;
  const ric = (window as unknown as {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number; }) => number;
  }).requestIdleCallback;
  if (typeof ric === "function") {
    ric(cb, { timeout: 2000 });
  } else {
    window.setTimeout(cb, 1200);
  }
};

/**
 * After the shell has mounted, warm the modules a signed-in user is most
 * likely to open next, during browser idle time so it never competes with the
 * current view's render or data fetches.
 */
export function prefetchLikelyRoutes(paths: readonly string[] = ["/ar", "/ap", "/inbox"]): void {
  scheduleIdle(() => {
    for (const p of paths) prefetchRoute(p);
  });
}
